import { useState, useEffect, useCallback, useRef } from 'react';
import { invoicesAPI, contactsAPI, taxReturnAPI, exportAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  FileInput, Upload, CheckCircle, Clock, Trash2, Eye, Loader2,
  Mail, Inbox, Send, ArrowRightLeft, Info, RotateCcw, Camera,
  FileText, Calendar, Filter, UserCheck, ExternalLink, RefreshCw,
  ArrowUp, ArrowDown, ArrowUpDown, FolderArchive, Paperclip
} from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';

const formatCurrency = (val, currency = 'EUR') =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(val || 0);

const statusConfig = {
  review: { label: 'Prüfung', variant: 'outline', icon: Clock, color: 'text-amber-400' },
  confirmed: { label: 'Bestätigt', variant: 'default', icon: CheckCircle, color: 'text-emerald-400' },
};

function SortableHeader({ sortKey, children, className = '', sortConfig, onToggle }) {
  const active = sortConfig.key === sortKey;
  const Icon = !active ? ArrowUpDown : sortConfig.direction === 'asc' ? ArrowUp : ArrowDown;
  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => onToggle(sortKey)}
        className={`flex items-center gap-1 hover:text-foreground transition-colors ${active ? 'text-primary' : 'text-muted-foreground'}`}
        data-testid={`sort-${sortKey}`}
      >
        <span>{children}</span>
        <Icon className="w-3 h-3" />
      </button>
    </TableHead>
  );
}

export default function IncomingInvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [inboxEmails, setInboxEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null); // { total, done, current, results }
  const [simulatingSend, setSimulatingSend] = useState(false);
  const [detailInvoice, setDetailInvoice] = useState(null);
  const [detailEmail, setDetailEmail] = useState(null);
  const [activeTab, setActiveTab] = useState('rechnungen');
  const [simulateForm, setSimulateForm] = useState({ sender: 'lieferant@example.de', subject: 'Rechnung' });
  // Filter state
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterMonth, setFilterMonth] = useState(0); // 0 = all months
  const [filterSupplier, setFilterSupplier] = useState('');
  const [zipLoading, setZipLoading] = useState(false);
  const [attachingId, setAttachingId] = useState(null);
  const attachInputRef = useRef(null);
  const attachTargetRef = useRef(null);

  const handleAttachClick = (invoice) => {
    attachTargetRef.current = invoice.id;
    if (attachInputRef.current) {
      attachInputRef.current.value = '';
      attachInputRef.current.click();
    }
  };

  const handleAttachFile = async (event) => {
    const file = event.target.files?.[0];
    const invoiceId = attachTargetRef.current;
    if (!file || !invoiceId) return;
    setAttachingId(invoiceId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await invoicesAPI.incoming.attachOriginal(invoiceId, formData);
      toast.success('Beleg wurde hinterlegt');
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Beleg konnte nicht gespeichert werden');
    } finally {
      setAttachingId(null);
      attachTargetRef.current = null;
    }
  };

  const handleReceiptsZip = async () => {
    setZipLoading(true);
    try {
      const res = await exportAPI.receiptsZip(filterYear, filterMonth > 0 ? filterMonth : null);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(res.data);
      link.download = `Belege_${filterYear}${filterMonth > 0 ? `-${String(filterMonth).padStart(2, '0')}` : ''}.zip`;
      link.click();
      URL.revokeObjectURL(link.href);
      const files = res.headers['x-file-count'];
      toast.success(`ZIP erstellt${files ? ` – ${files} Belege enthalten` : ''}`);
    } catch (err) {
      toast.error(err.response?.status === 404
        ? 'Für diesen Zeitraum gibt es keine Eingangsrechnungen'
        : 'ZIP konnte nicht erstellt werden');
    } finally {
      setZipLoading(false);
    }
  };
  const [contacts, setContacts] = useState([]);
  const [assigningSupplier, setAssigningSupplier] = useState(null);
  const [euerCategories, setEuerCategories] = useState([]);
  // Sort state — sortable column headers
  const [sortConfig, setSortConfig] = useState({ key: 'invoice_date', direction: 'desc' });

  const toggleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return { key: 'invoice_date', direction: 'desc' };
    });
  };

  const sortInvoices = useCallback((list) => {
    if (!sortConfig?.key) return list;
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    const key = sortConfig.key;
    const getVal = (inv) => {
      const ocr = inv.ocr_data || {};
      switch (key) {
        case 'source': return inv.source || '';
        case 'filename': return (inv.filename || '').toLowerCase();
        case 'vendor': return (ocr.vendor_name || '').toLowerCase();
        case 'invoice_number': return (ocr.invoice_number || '').toLowerCase();
        case 'gross': return Number(ocr.gross_amount || 0);
        case 'currency': return (ocr.original_currency || ocr.currency || 'EUR').toLowerCase();
        case 'status': return inv.status || '';
        case 'confidence': return Number(ocr.confidence_score || 0);
        case 'invoice_date': return ocr.invoice_date || inv.created_at || '';
        default: return '';
      }
    };
    return [...list].sort((a, b) => {
      const va = getVal(a); const vb = getVal(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [sortConfig]);

  useEffect(() => {
    taxReturnAPI.categories()
      .then((res) => setEuerCategories(res.data.categories || []))
      .catch(() => setEuerCategories([]));
  }, []);

  const handleSetCategory = async (invId, code) => {
    try {
      await invoicesAPI.incoming.setExpenseCategory(invId, code);
      setInvoices((prev) => prev.map((i) => (i.id === invId ? { ...i, expense_category: code, expense_category_source: 'manual' } : i)));
      toast.success('EÜR-Position zugeordnet');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Zuordnung fehlgeschlagen');
    }
  };

  const loadData = useCallback(async () => {
    try {
      const params = {};
      if (filterYear) params.year = filterYear;
      if (filterMonth > 0) params.month = filterMonth;
      if (filterSupplier) params.supplier_id = filterSupplier;
      const [invRes, inboxRes, contactsRes] = await Promise.all([
        invoicesAPI.incoming.list(params),
        invoicesAPI.inbox.list(),
        contactsAPI.list(),
      ]);
      setInvoices(invRes.data.invoices || []);
      setInboxEmails(inboxRes.data.emails || []);
      setContacts(contactsRes.data.contacts || []);
    } catch (err) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, [filterYear, filterMonth, filterSupplier]);

  useEffect(() => { loadData(); }, [loadData]);

  // Handlers for new features
  const handleOpenOriginal = async (inv) => {
    try {
      const res = await invoicesAPI.incoming.getOriginal(inv.id);
      const blob = new Blob([res.data], { type: inv.content_type || 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch {
      toast.error('Originalbeleg nicht verfügbar');
    }
  };

  const handleAssignSupplier = async (invId, supplierId) => {
    try {
      await invoicesAPI.incoming.assignSupplier(invId, { supplier_id: supplierId });
      toast.success('Lieferant zugeordnet');
      setAssigningSupplier(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    }
  };

  // Batch upload (drag & drop, multiple files)
  const onDrop = useCallback(async (acceptedFiles) => {
    if (!acceptedFiles.length) return;
    setUploading(true);
    const total = acceptedFiles.length;
    const results = [];
    setBatchProgress({ total, done: 0, current: acceptedFiles[0].name, results: [] });

    for (let i = 0; i < acceptedFiles.length; i++) {
      const file = acceptedFiles[i];
      setBatchProgress(prev => ({ ...prev, current: file.name, done: i }));
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await invoicesAPI.incoming.upload(formData);
        results.push({ file: file.name, success: true, data: res.data });
      } catch (err) {
        results.push({ file: file.name, success: false, error: err.response?.data?.detail || 'Fehler' });
      }
      setBatchProgress(prev => ({ ...prev, done: i + 1, results: [...results] }));
    }

    const ok = results.filter(r => r.success).length;
    const fail = results.filter(r => !r.success).length;
    if (fail === 0) {
      toast.success(`${ok} Rechnung${ok > 1 ? 'en' : ''} erfolgreich analysiert`);
    } else {
      toast.warning(`${ok} erfolgreich, ${fail} fehlgeschlagen`);
    }

    setUploading(false);
    // Keep progress visible for a moment, then clear
    setTimeout(() => setBatchProgress(null), 5000);
    loadData();
  }, [loadData]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'], 'image/*': ['.png', '.jpg', '.jpeg'] },
    multiple: true,
    disabled: uploading,
  });

  const handleCameraCapture = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await invoicesAPI.incoming.upload(formData);
      toast.success('Foto-Rechnung erfolgreich analysiert');
      setDetailInvoice(res.data);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setUploading(false);
    }
  }, [loadData]);

  // Simulate email inbox
  const simulateEmailFileRef = useCallback((node) => {
    if (node) node.simulateRef = node;
  }, []);

  const handleSimulateEmail = async (e) => {
    e.preventDefault();
    const fileInput = document.getElementById('simulate-file-input');
    const file = fileInput?.files?.[0];
    if (!file) { toast.error('Bitte Datei auswählen'); return; }
    
    setSimulatingSend(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('sender_email', simulateForm.sender);
      formData.append('subject', simulateForm.subject);
      const res = await invoicesAPI.inbox.simulate(formData);
      toast.success(`E-Mail simuliert: ${res.data.processed?.length || 0} Rechnung(en) verarbeitet`);
      loadData();
      fileInput.value = '';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler bei E-Mail-Simulation');
    } finally {
      setSimulatingSend(false);
    }
  };

  const handleConfirm = async (id, reverseCharge = false, rcTaxRate = 19) => {
    try {
      await invoicesAPI.incoming.confirm(id, { reverse_charge: reverseCharge, reverse_charge_tax_rate: rcTaxRate });
      toast.success('Rechnung bestätigt');
      loadData();
      setDetailInvoice(null);
    } catch { toast.error('Fehler beim Bestätigen'); }
  };

  const handleToggleRC = async (inv, isRC, rcRate = 19) => {
    try {
      await invoicesAPI.incoming.toggleReverseCharge(inv.id, { reverse_charge: isRC, reverse_charge_tax_rate: rcRate });
      toast.success(isRC ? 'Reverse-Charge aktiviert (§13b UStG)' : 'Reverse-Charge deaktiviert');
      loadData();
      // Update detail view if open
      if (detailInvoice?.id === inv.id) {
        setDetailInvoice(prev => ({
          ...prev,
          reverse_charge: isRC,
          reverse_charge_tax_rate: rcRate,
          reverse_charge_tax_amount: isRC ? (prev.ocr_data?.net_amount || 0) * (rcRate / 100) : 0,
        }));
      }
    } catch { toast.error('Fehler beim Ändern'); }
  };

  const handleDelete = async (id) => {
    try {
      await invoicesAPI.incoming.remove(id);
      toast.success('Rechnung gelöscht');
      loadData();
    } catch { toast.error('Fehler beim Löschen'); }
  };

  const handleDeleteEmail = async (id) => {
    try {
      await invoicesAPI.inbox.remove(id);
      toast.success('E-Mail gelöscht');
      loadData();
    } catch { toast.error('Fehler beim Löschen'); }
  };

  const emailInvoices = invoices.filter(inv => inv.source === 'email');
  const uploadInvoices = invoices.filter(inv => inv.source !== 'email');

  return (
    <div className="space-y-8" data-testid="incoming-invoices-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight mb-2">Eingangsrechnungen</h1>
          <p className="text-sm sm:text-base text-muted-foreground">Rechnungen hochladen oder per E-Mail empfangen und automatisch auswerten</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="incoming-tabs">
        <TabsList className="bg-card border border-border/50 flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="rechnungen" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary text-xs sm:text-sm" data-testid="tab-rechnungen">
            <FileInput className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Alle Rechnungen</span>
            <span className="sm:hidden">Alle</span>
            <span className="ml-1">({invoices.length})</span>
          </TabsTrigger>
          <TabsTrigger value="upload" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary text-xs sm:text-sm" data-testid="tab-upload">
            <Upload className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            Upload
          </TabsTrigger>
          <TabsTrigger value="email" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary text-xs sm:text-sm" data-testid="tab-email-inbox">
            <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">E-Mail Posteingang</span>
            <span className="sm:hidden">E-Mail</span>
            <span className="ml-1">({inboxEmails.length})</span>
          </TabsTrigger>
        </TabsList>

        {/* ==================== ALL INVOICES TAB ==================== */}
        <input
          ref={attachInputRef}
          type="file"
          accept="application/pdf,image/png,image/jpeg"
          className="hidden"
          onChange={handleAttachFile}
          data-testid="attach-original-input"
        />

        <TabsContent value="rechnungen" className="mt-6">
          {/* Filter Bar */}
          <Card className="bg-card border border-border/50 mb-4" data-testid="filter-bar">
            <CardContent className="py-3 px-4">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Filter className="w-3.5 h-3.5" />
                  Filter:
                </div>
                <Select value={String(filterYear)} onValueChange={v => setFilterYear(Number(v))}>
                  <SelectTrigger className="w-24 h-8 text-xs bg-background" data-testid="filter-year">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map(y => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={String(filterMonth)} onValueChange={v => setFilterMonth(Number(v))}>
                  <SelectTrigger className="w-32 h-8 text-xs bg-background" data-testid="filter-month">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Alle Monate</SelectItem>
                    {['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'].map((m, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterSupplier} onValueChange={v => setFilterSupplier(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-40 h-8 text-xs bg-background" data-testid="filter-supplier">
                    <SelectValue placeholder="Alle Lieferanten" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Lieferanten</SelectItem>
                    {contacts.filter(c => c.contact_type === 'supplier' || c.contact_type === 'Lieferant').map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(filterMonth > 0 || filterSupplier) && (
                  <Button variant="ghost" size="sm" onClick={() => { setFilterMonth(0); setFilterSupplier(''); }} className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground" data-testid="filter-reset">
                    <RotateCcw className="w-3 h-3 mr-1" /> Zurücksetzen
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReceiptsZip}
                  disabled={zipLoading}
                  className="h-8 px-2.5 text-xs ml-auto"
                  title="Alle Originalbelege des gewählten Zeitraums als ZIP"
                  data-testid="download-receipts-zip-btn"
                >
                  {zipLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FolderArchive className="w-3 h-3 mr-1" />}
                  Belege als ZIP
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border border-border/50" data-testid="incoming-invoices-table">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <FileInput className="w-4 h-4 text-primary" />
                Eingangsrechnungen ({invoices.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="py-12 text-center text-muted-foreground">Laden...</div>
              ) : invoices.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground">
                  <FileInput className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Noch keine Eingangsrechnungen vorhanden</p>
                  <p className="text-sm mt-1">Laden Sie eine Rechnung hoch oder simulieren Sie einen E-Mail-Eingang</p>
                </div>
              ) : (
                <div className="table-responsive">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/30">
                      <SortableHeader sortKey="source" sortConfig={sortConfig} onToggle={toggleSort}>Quelle</SortableHeader>
                      <SortableHeader sortKey="filename" className="hidden sm:table-cell" sortConfig={sortConfig} onToggle={toggleSort}>Datei</SortableHeader>
                      <SortableHeader sortKey="vendor" sortConfig={sortConfig} onToggle={toggleSort}>Lieferant</SortableHeader>
                      <SortableHeader sortKey="invoice_date" className="hidden md:table-cell" sortConfig={sortConfig} onToggle={toggleSort}>Datum</SortableHeader>
                      <SortableHeader sortKey="invoice_number" className="hidden md:table-cell" sortConfig={sortConfig} onToggle={toggleSort}>Rechnungsnr.</SortableHeader>
                      <SortableHeader sortKey="gross" className="text-right" sortConfig={sortConfig} onToggle={toggleSort}>Brutto (EUR)</SortableHeader>
                      <SortableHeader sortKey="currency" className="hidden lg:table-cell" sortConfig={sortConfig} onToggle={toggleSort}>Währung</SortableHeader>
                      <TableHead className="hidden xl:table-cell">EÜR-Position</TableHead>
                      <SortableHeader sortKey="status" sortConfig={sortConfig} onToggle={toggleSort}>Status</SortableHeader>
                      <SortableHeader sortKey="confidence" className="hidden md:table-cell" sortConfig={sortConfig} onToggle={toggleSort}>Konfidenz</SortableHeader>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortInvoices(invoices).map((inv) => {
                      const ocr = inv.ocr_data || {};
                      const sc = statusConfig[inv.status] || statusConfig.review;
                      const wasConverted = ocr.currency_converted;
                      const dateRaw = ocr.invoice_date || inv.created_at;
                      let dateFmt = '-';
                      if (dateRaw) {
                        const d = new Date(dateRaw);
                        if (!isNaN(d.getTime())) {
                          dateFmt = `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`;
                        }
                      }
                      return (
                        <TableRow key={inv.id} className="border-border/20 hover:bg-accent/50 transition-colors" data-testid={`invoice-row-${inv.id}`}>
                          <TableCell>
                            {inv.source === 'email' ? (
                              <Badge variant="outline" className="text-xs text-blue-400 border-blue-400/30">
                                <Mail className="w-3 h-3 mr-1" /> E-Mail
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs text-muted-foreground">
                                <Upload className="w-3 h-3 mr-1" /> Upload
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="font-mono-data text-sm max-w-[120px] truncate hidden sm:table-cell">{inv.filename}</TableCell>
                          <TableCell className="max-w-[100px] truncate">{ocr.vendor_name || '-'}</TableCell>
                          <TableCell className="font-mono-data text-sm hidden md:table-cell" data-testid={`invoice-date-${inv.id}`}>{dateFmt}</TableCell>
                          <TableCell className="font-mono-data hidden md:table-cell">{ocr.invoice_number || '-'}</TableCell>
                          <TableCell className="text-right font-mono-data font-bold">
                            {formatCurrency(ocr.gross_amount)}
                          </TableCell>
                          <TableCell className="hidden lg:table-cell">
                            {wasConverted ? (
                              <div className="flex items-center gap-1">
                                <ArrowRightLeft className="w-3 h-3 text-amber-400" />
                                <span className="text-xs font-mono-data text-amber-400">
                                  {ocr.original_currency}
                                </span>
                              </div>
                            ) : (
                              <span className="text-xs font-mono-data text-muted-foreground">EUR</span>
                            )}
                          </TableCell>
                          <TableCell className="hidden xl:table-cell">
                            <Select
                              value={inv.expense_category || ''}
                              onValueChange={(v) => handleSetCategory(inv.id, v)}
                            >
                              <SelectTrigger
                                className={`h-7 w-[190px] text-xs ${inv.expense_category ? 'bg-background' : 'bg-amber-500/5 border-amber-500/40 text-amber-400'}`}
                                data-testid={`euer-category-select-${inv.id}`}
                              >
                                <SelectValue placeholder="Nicht zugeordnet" />
                              </SelectTrigger>
                              <SelectContent className="max-h-72">
                                {euerCategories.map((c) => (
                                  <SelectItem key={c.code} value={c.code} className="text-xs">
                                    Z{c.line} · Kz {c.kz} – {c.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {inv.expense_category_source === 'ai' && (
                              <span className="text-[10px] text-cyan-400/70 font-mono-data">KI-Vorschlag</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5">
                              <Badge variant={sc.variant} className={`${sc.color} border-current/20`}>
                                <sc.icon className="w-3 h-3 mr-1" />
                                {sc.label}
                              </Badge>
                              {inv.reverse_charge && (
                                <Badge className="bg-violet-500/10 text-violet-400 border-violet-500/20 text-[10px]" data-testid={`rc-badge-${inv.id}`}>
                                  <RotateCcw className="w-2.5 h-2.5 mr-0.5" />
                                  RC
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            <div className="flex items-center gap-1.5">
                              <div className={`w-2 h-2 rounded-full ${
                                (ocr.confidence_score || 0) > 0.8 ? 'bg-emerald-500' :
                                (ocr.confidence_score || 0) > 0.5 ? 'bg-amber-500' : 'bg-red-500'
                              }`} />
                              <span className="font-mono-data text-xs">
                                {((ocr.confidence_score || 0) * 100).toFixed(0)}%
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end flex-wrap">
                              <Button variant="ghost" size="sm" onClick={() => setDetailInvoice(inv)} className="h-7 w-7 p-0" data-testid={`view-invoice-${inv.id}`}>
                                <Eye className="w-3.5 h-3.5" />
                              </Button>
                              {inv.has_original ? (
                                <Button variant="ghost" size="sm" onClick={() => handleOpenOriginal(inv)} className="h-7 w-7 p-0 text-cyan-400" title="Originalbeleg anzeigen" data-testid={`original-invoice-${inv.id}`}>
                                  <FileText className="w-3.5 h-3.5" />
                                </Button>
                              ) : (
                                <Button variant="ghost" size="sm" onClick={() => handleAttachClick(inv)} disabled={attachingId === inv.id} className="h-7 w-7 p-0 text-amber-400" title="Beleg nachtragen (PDF oder Bild)" data-testid={`attach-original-${inv.id}`}>
                                  {attachingId === inv.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
                                </Button>
                              )}
                              {!inv.supplier_id && (
                                <Button variant="ghost" size="sm" onClick={() => setAssigningSupplier(inv)} className="h-7 w-7 p-0 text-amber-400" title="Lieferant zuordnen" data-testid={`assign-supplier-${inv.id}`}>
                                  <UserCheck className="w-3.5 h-3.5" />
                                </Button>
                              )}
                              {inv.status === 'review' && (
                                <Button variant="ghost" size="sm" onClick={() => handleConfirm(inv.id)} className="h-7 w-7 p-0 text-emerald-400 hover:text-emerald-300" data-testid={`confirm-invoice-${inv.id}`}>
                                  <CheckCircle className="w-3.5 h-3.5" />
                                </Button>
                              )}
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(inv.id)} className="h-7 w-7 p-0 text-destructive hover:text-destructive" data-testid={`delete-invoice-${inv.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== UPLOAD TAB ==================== */}
        <TabsContent value="upload" className="mt-6">
          <Card className="bg-card border border-dashed border-primary/30 hover:border-primary/60 transition-colors duration-300" data-testid="upload-area">
            <CardContent className="p-0">
              <div
                {...getRootProps()}
                className={`flex flex-col items-center justify-center py-12 sm:py-20 px-4 sm:px-8 cursor-pointer transition-all ${
                  isDragActive ? 'bg-primary/5' : ''
                } ${uploading ? 'pointer-events-none opacity-60' : ''}`}
              >
                <input {...getInputProps()} data-testid="file-upload-input" />
                {uploading && batchProgress ? (
                  <>
                    <Loader2 className="w-10 h-10 sm:w-14 sm:h-14 text-primary mb-4 animate-spin" />
                    <p className="text-base sm:text-lg font-heading font-semibold mb-1">
                      KI analysiert Rechnungen... ({batchProgress.done}/{batchProgress.total})
                    </p>
                    <p className="text-xs sm:text-sm text-muted-foreground text-center mb-3">
                      Aktuelle Datei: {batchProgress.current}
                    </p>
                    <div className="w-full max-w-xs bg-accent/50 rounded-full h-2 overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all duration-500"
                        style={{ width: `${(batchProgress.done / batchProgress.total) * 100}%` }}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-primary/10 flex items-center justify-center mb-4 sm:mb-6">
                      <Upload className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
                    </div>
                    <p className="text-lg sm:text-xl font-heading font-semibold mb-2 text-center">
                      {isDragActive ? 'Dateien hier ablegen' : 'Rechnungen hochladen'}
                    </p>
                    <p className="text-xs sm:text-sm text-muted-foreground max-w-md text-center">
                      PDF oder Bilder hierher ziehen oder klicken. <strong>Mehrere Dateien gleichzeitig</strong> möglich. Gemini Flash OCR erkennt automatisch alle Rechnungsdaten.
                    </p>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Batch Results */}
          {batchProgress && batchProgress.results.length > 0 && !uploading && (
            <Card className="bg-card border border-border/50 mt-4" data-testid="batch-results">
              <CardContent className="p-4">
                <p className="text-sm font-medium mb-3">Ergebnis: {batchProgress.results.filter(r => r.success).length}/{batchProgress.total} erfolgreich</p>
                <div className="space-y-2">
                  {batchProgress.results.map((r, i) => (
                    <div key={i} className={`flex items-center justify-between text-sm p-2 rounded ${r.success ? 'bg-emerald-500/5' : 'bg-red-500/5'}`}>
                      <span className="truncate flex-1">{r.file}</span>
                      {r.success ? (
                        <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-xs ml-2">
                          <CheckCircle className="w-3 h-3 mr-1" /> Analysiert
                        </Badge>
                      ) : (
                        <Badge className="bg-red-500/10 text-red-400 border-red-500/20 text-xs ml-2">
                          Fehler
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Camera Capture Button */}
          <div className="mt-4 flex justify-center">
            <label
              htmlFor="camera-capture"
              className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 cursor-pointer transition-colors ${uploading ? 'pointer-events-none opacity-50' : ''}`}
              data-testid="camera-capture-btn"
            >
              <Camera className="w-5 h-5" />
              <span className="font-medium text-sm sm:text-base">Rechnung abfotografieren</span>
            </label>
            <input
              id="camera-capture"
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handleCameraCapture}
              disabled={uploading}
              data-testid="camera-capture-input"
            />
          </div>

          {/* Recent uploads */}
          {uploadInvoices.length > 0 && (
            <Card className="bg-card border border-border/50 mt-6">
              <CardHeader>
                <CardTitle className="font-heading text-base">Letzte Uploads ({uploadInvoices.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {uploadInvoices.slice(0, 5).map(inv => (
                    <div key={inv.id} className="flex items-center justify-between py-2 border-b border-border/20 last:border-0">
                      <div className="flex items-center gap-3">
                        <Upload className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-mono-data">{inv.filename}</span>
                        <span className="text-sm text-muted-foreground">{inv.ocr_data?.vendor_name || '-'}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        {inv.ocr_data?.currency_converted && (
                          <span className="text-xs text-amber-400 font-mono-data">
                            {inv.ocr_data.original_currency} → EUR
                          </span>
                        )}
                        <span className="font-mono-data font-bold text-sm">{formatCurrency(inv.ocr_data?.gross_amount)}</span>
                        <Button variant="ghost" size="sm" onClick={() => setDetailInvoice(inv)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ==================== EMAIL INBOX TAB ==================== */}
        <TabsContent value="email" className="mt-6 space-y-6">
          {/* How it works info */}
          <Card className="bg-card border border-primary/20" data-testid="email-inbox-info">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Inbox className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-bold text-lg mb-2">Rechnungseingang per E-Mail</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Lieferanten können Rechnungen per E-Mail senden. Die Anhänge werden automatisch per KI analysiert,
                    Rechnungsdaten extrahiert und Fremdwährungen in EUR umgerechnet.
                  </p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground bg-accent/50 rounded-lg px-3 py-2 inline-flex">
                    <Info className="w-3.5 h-3.5" />
                    <span>Nutzen Sie die Simulation unten, um den E-Mail-Eingang zu testen</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Simulate Email */}
          <Card className="bg-card border border-border/50" data-testid="simulate-email-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Send className="w-4 h-4 text-secondary" />
                E-Mail-Eingang simulieren
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSimulateEmail} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Absender E-Mail</Label>
                    <Input
                      value={simulateForm.sender}
                      onChange={(e) => setSimulateForm({ ...simulateForm, sender: e.target.value })}
                      placeholder="lieferant@firma.de"
                      className="bg-background h-10"
                      data-testid="simulate-sender-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Betreff</Label>
                    <Input
                      value={simulateForm.subject}
                      onChange={(e) => setSimulateForm({ ...simulateForm, subject: e.target.value })}
                      placeholder="Rechnung Nr. 12345"
                      className="bg-background h-10"
                      data-testid="simulate-subject-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Rechnungs-Anhang (PDF/Bild)</Label>
                  <Input
                    id="simulate-file-input"
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    className="bg-background h-10 file:bg-primary/10 file:text-primary file:border-0 file:rounded file:px-3 file:py-1 file:mr-3 file:text-sm"
                    data-testid="simulate-file-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={simulatingSend}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_15px_rgba(0,229,255,0.3)]"
                  data-testid="simulate-send-btn"
                >
                  {simulatingSend ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Wird verarbeitet...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      E-Mail simulieren & verarbeiten
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Email Inbox List */}
          <Card className="bg-card border border-border/50" data-testid="email-inbox-list">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Inbox className="w-4 h-4 text-primary" />
                Posteingang ({inboxEmails.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {inboxEmails.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground">
                  <Mail className="w-12 h-12 mx-auto mb-3 opacity-20" />
                  <p>Noch keine E-Mails empfangen</p>
                  <p className="text-sm mt-1">Simulieren Sie einen E-Mail-Eingang oben</p>
                </div>
              ) : (
                <div className="table-responsive">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/30">
                      <TableHead>Absender</TableHead>
                      <TableHead className="hidden sm:table-cell">Betreff</TableHead>
                      <TableHead className="hidden md:table-cell">Empfangen</TableHead>
                      <TableHead className="hidden lg:table-cell">Anhänge</TableHead>
                      <TableHead className="hidden lg:table-cell">Verarbeitete Rechnungen</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inboxEmails.map((email) => (
                      <TableRow
                        key={email.id}
                        className="border-border/20 hover:bg-accent/50 cursor-pointer"
                        data-testid={`inbox-row-${email.id}`}
                        onClick={() => setDetailEmail(email)}
                      >
                        <TableCell className="text-sm max-w-[120px] truncate">{email.from_email}</TableCell>
                        <TableCell className="text-sm font-medium hidden sm:table-cell">{email.subject}</TableCell>
                        <TableCell className="text-xs font-mono-data text-muted-foreground hidden md:table-cell">
                          {new Date(email.received_at).toLocaleString('de-DE')}
                        </TableCell>
                        <TableCell className="font-mono-data text-sm hidden lg:table-cell">{email.attachments_count}</TableCell>
                        <TableCell className="hidden lg:table-cell">
                          {(email.processed_invoices || []).length > 0 ? (
                            <div className="space-y-1">
                              {email.processed_invoices.map((pi, i) => (
                                <div key={i} className="text-xs">
                                  <span className="text-primary font-mono-data">{pi.filename}</span>
                                  {pi.vendor && <span className="text-muted-foreground ml-1">({pi.vendor})</span>}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">Keine</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={
                            email.status === 'processed' ? 'text-emerald-400 border-emerald-400/30' :
                            'text-amber-400 border-amber-400/30'
                          }>
                            {email.status === 'processed' ? 'Verarbeitet' : email.status === 'new' ? 'Neu' : 'Keine Anhänge'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDetailEmail(email)}
                              className="text-primary"
                              data-testid={`view-email-${email.id}`}
                              title="E-Mail-Inhalt anzeigen"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteEmail(email.id)} className="text-destructive" data-testid={`delete-email-${email.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ==================== DETAIL DIALOG ==================== */}
      {detailInvoice && (
      <Dialog open={!!detailInvoice} onOpenChange={() => setDetailInvoice(null)}>
        <DialogContent className="bg-card border-border/50 max-w-2xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-auto" data-testid="invoice-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-3">
              Rechnungsdetails
              {detailInvoice?.ocr_data?.confidence_score && (
                <Badge variant="outline" className="text-xs font-mono-data">
                  Konfidenz: {((detailInvoice.ocr_data.confidence_score || 0) * 100).toFixed(0)}%
                </Badge>
              )}
              {detailInvoice?.source === 'email' && (
                <Badge variant="outline" className="text-xs text-blue-400 border-blue-400/30">
                  <Mail className="w-3 h-3 mr-1" /> via E-Mail
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          {detailInvoice && (
            <div className="space-y-4">
              {/* Currency Conversion Banner */}
              {detailInvoice.ocr_data?.currency_converted && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 flex items-center gap-3" data-testid="currency-conversion-banner">
                  <ArrowRightLeft className="w-5 h-5 text-amber-400 flex-shrink-0" />
                  <div className="text-sm">
                    <span className="text-amber-400 font-semibold">Währung konvertiert: </span>
                    <span className="font-mono-data">
                      {formatCurrency(detailInvoice.ocr_data.original_gross_amount, detailInvoice.ocr_data.original_currency)}
                      {' → '}
                      {formatCurrency(detailInvoice.ocr_data.gross_amount, 'EUR')}
                    </span>
                    <span className="text-muted-foreground ml-2">
                      (Kurs: 1 {detailInvoice.ocr_data.original_currency} = {detailInvoice.ocr_data.exchange_rate} EUR)
                    </span>
                  </div>
                </div>
              )}

              {/* Reverse-Charge Section */}
              {detailInvoice.confirmed && (
                <div className={`rounded-lg p-4 border ${detailInvoice.reverse_charge ? 'bg-violet-500/5 border-violet-500/20' : 'bg-accent/30 border-border/30'}`} data-testid="reverse-charge-section">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <RotateCcw className={`w-5 h-5 ${detailInvoice.reverse_charge ? 'text-violet-400' : 'text-muted-foreground'}`} />
                      <div>
                        <p className="text-sm font-semibold">Reverse-Charge (§13b UStG)</p>
                        <p className="text-xs text-muted-foreground">Steuerschuldnerschaft des Leistungsempfängers</p>
                      </div>
                    </div>
                    <Switch
                      checked={detailInvoice.reverse_charge || false}
                      onCheckedChange={(checked) => handleToggleRC(detailInvoice, checked, detailInvoice.reverse_charge_tax_rate || 19)}
                      data-testid="reverse-charge-toggle"
                    />
                  </div>
                  {detailInvoice.reverse_charge && (
                    <div className="mt-3 pt-3 border-t border-violet-500/20 space-y-2">
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground">Steuersatz:</span>
                        <Select
                          value={String(detailInvoice.reverse_charge_tax_rate || 19)}
                          onValueChange={(v) => handleToggleRC(detailInvoice, true, Number(v))}
                        >
                          <SelectTrigger className="w-24 h-7 text-xs bg-background" data-testid="rc-tax-rate-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="19">19%</SelectItem>
                            <SelectItem value="7">7%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="bg-violet-500/10 rounded-md p-3 space-y-1.5 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Bemessungsgrundlage (Netto)</span>
                          <span className="font-mono-data">{formatCurrency(detailInvoice.ocr_data?.net_amount || detailInvoice.ocr_data?.gross_amount)}</span>
                        </div>
                        <div className="flex justify-between text-red-400">
                          <span>+ Umsatzsteuer §13b ({detailInvoice.reverse_charge_tax_rate || 19}%)</span>
                          <span className="font-mono-data font-bold">{formatCurrency(detailInvoice.reverse_charge_tax_amount || ((detailInvoice.ocr_data?.net_amount || 0) * ((detailInvoice.reverse_charge_tax_rate || 19) / 100)))}</span>
                        </div>
                        <div className="flex justify-between text-emerald-400">
                          <span>- Vorsteuerabzug §13b ({detailInvoice.reverse_charge_tax_rate || 19}%)</span>
                          <span className="font-mono-data font-bold">-{formatCurrency(detailInvoice.reverse_charge_tax_amount || ((detailInvoice.ocr_data?.net_amount || 0) * ((detailInvoice.reverse_charge_tax_rate || 19) / 100)))}</span>
                        </div>
                        <div className="flex justify-between border-t border-violet-500/20 pt-1.5 font-semibold text-violet-400">
                          <span>= Effekt auf Zahllast</span>
                          <span className="font-mono-data">0,00 EUR</span>
                        </div>
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        Bei Reverse-Charge wird die Steuer in der UStVA sowohl als Umsatzsteuer als auch als Vorsteuer ausgewiesen (Nullsummenspiel).
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Email source info */}
              {detailInvoice.source === 'email' && (
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 flex items-center gap-3">
                  <Mail className="w-5 h-5 text-blue-400 flex-shrink-0" />
                  <div className="text-sm">
                    <span className="text-blue-400 font-semibold">Empfangen von: </span>
                    <span className="font-mono-data">{detailInvoice.source_email}</span>
                    {detailInvoice.source_subject && (
                      <span className="text-muted-foreground ml-2">– {detailInvoice.source_subject}</span>
                    )}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                {[
                  ['Lieferant', detailInvoice.ocr_data?.vendor_name],
                  ['Adresse', detailInvoice.ocr_data?.vendor_address],
                  ['Rechnungsnr.', detailInvoice.ocr_data?.invoice_number],
                  ['Datum', detailInvoice.ocr_data?.invoice_date],
                  ['Fällig', detailInvoice.ocr_data?.due_date],
                  ['Währung', detailInvoice.ocr_data?.currency_converted
                    ? `EUR (konvertiert aus ${detailInvoice.ocr_data?.original_currency})`
                    : (detailInvoice.ocr_data?.currency || 'EUR')
                  ],
                ].map(([label, value]) => (
                  <div key={label}>
                    <p className="text-xs text-muted-foreground mb-1">{label}</p>
                    <p className="font-medium text-sm">{value || '-'}</p>
                  </div>
                ))}
              </div>

              {detailInvoice.ocr_data?.items && detailInvoice.ocr_data.items.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-2 font-medium uppercase tracking-wider">Positionen</p>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border/30">
                        <TableHead>Beschreibung</TableHead>
                        <TableHead className="text-right">Menge</TableHead>
                        <TableHead className="text-right">Einzelpreis (EUR)</TableHead>
                        <TableHead className="text-right">Gesamt (EUR)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detailInvoice.ocr_data.items.map((item, i) => (
                        <TableRow key={i} className="border-border/20">
                          <TableCell className="text-sm">{item.description}</TableCell>
                          <TableCell className="text-right font-mono-data">{item.quantity}</TableCell>
                          <TableCell className="text-right font-mono-data">
                            {formatCurrency(item.unit_price)}
                            {item.original_unit_price && (
                              <span className="text-xs text-muted-foreground block">
                                ({formatCurrency(item.original_unit_price, detailInvoice.ocr_data?.original_currency)})
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-mono-data font-bold">
                            {formatCurrency(item.total)}
                            {item.original_total && (
                              <span className="text-xs text-muted-foreground block">
                                ({formatCurrency(item.original_total, detailInvoice.ocr_data?.original_currency)})
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              <div className="bg-accent/50 rounded-lg p-4 space-y-2">
                {detailInvoice.ocr_data?.currency_converted && (
                  <>
                    <div className="flex justify-between text-xs text-muted-foreground pb-2 border-b border-border/30 mb-2">
                      <span>Originalbeträge ({detailInvoice.ocr_data.original_currency})</span>
                      <span>
                        Netto: {formatCurrency(detailInvoice.ocr_data.original_net_amount, detailInvoice.ocr_data.original_currency)} | 
                        Brutto: {formatCurrency(detailInvoice.ocr_data.original_gross_amount, detailInvoice.ocr_data.original_currency)}
                      </span>
                    </div>
                  </>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Netto (EUR)</span>
                  <span className="font-mono-data">{formatCurrency(detailInvoice.ocr_data?.net_amount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">USt ({detailInvoice.ocr_data?.tax_rate || 19}%)</span>
                  <span className="font-mono-data">{formatCurrency(detailInvoice.ocr_data?.tax_amount)}</span>
                </div>
                <div className="flex justify-between text-base font-bold border-t border-border/30 pt-2 mt-2">
                  <span>Brutto (EUR)</span>
                  <span className="font-mono-data text-primary">{formatCurrency(detailInvoice.ocr_data?.gross_amount)}</span>
                </div>
              </div>

              {detailInvoice.ocr_data?.payment_info && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Zahlungsinformationen</p>
                  <p className="text-sm bg-accent/30 rounded-lg p-3 font-mono-data">{detailInvoice.ocr_data.payment_info}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex-wrap gap-2">
            {detailInvoice?.status === 'review' && (
              <>
                <Button
                  onClick={() => handleConfirm(detailInvoice.id, true, 19)}
                  variant="outline"
                  className="text-violet-400 border-violet-400/30 hover:bg-violet-500/10"
                  data-testid="confirm-rc-dialog-btn"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Bestätigen als Reverse-Charge
                </Button>
                <Button
                  onClick={() => handleConfirm(detailInvoice.id, false)}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="confirm-invoice-dialog-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Bestätigen
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => setDetailInvoice(null)} data-testid="close-detail-btn">
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}

      {/* ==================== EMAIL DETAIL DIALOG ==================== */}
      {detailEmail && (
        <Dialog open={!!detailEmail} onOpenChange={() => setDetailEmail(null)}>
          <DialogContent className="bg-card border-border/50 max-w-3xl max-h-[90vh] overflow-y-auto w-[95vw]" data-testid="email-detail-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-3">
                <Mail className="w-5 h-5 text-primary" />
                E-Mail-Details
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Header info grid */}
              <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-y-2 gap-x-3 text-sm border border-border/30 rounded-lg p-4 bg-background/30">
                <div className="text-muted-foreground">Absender:</div>
                <div className="font-mono-data break-all" data-testid="email-detail-from">{detailEmail.from_email}</div>
                <div className="text-muted-foreground">Betreff:</div>
                <div className="font-medium" data-testid="email-detail-subject">{detailEmail.subject || '(kein Betreff)'}</div>
                <div className="text-muted-foreground">Empfangen:</div>
                <div className="font-mono-data text-muted-foreground">
                  {new Date(detailEmail.received_at).toLocaleString('de-DE')}
                </div>
                <div className="text-muted-foreground">Status:</div>
                <div>
                  <Badge variant="outline" className={
                    detailEmail.status === 'processed' ? 'text-emerald-400 border-emerald-400/30' :
                    'text-amber-400 border-amber-400/30'
                  }>
                    {detailEmail.status === 'processed' ? 'Verarbeitet' :
                     detailEmail.status === 'new' ? 'Neu' : 'Keine Anhänge'}
                  </Badge>
                </div>
                <div className="text-muted-foreground">Anhänge:</div>
                <div className="font-mono-data">{detailEmail.attachments_count ?? 0}</div>
              </div>

              {/* Body */}
              <div>
                <h4 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Nachrichteninhalt</h4>
                {detailEmail.body ? (
                  <pre
                    className="text-sm whitespace-pre-wrap break-words bg-background/50 border border-border/30 rounded-lg p-4 max-h-[40vh] overflow-y-auto font-sans"
                    data-testid="email-detail-body"
                  >
                    {detailEmail.body}
                  </pre>
                ) : (
                  <p className="text-sm text-muted-foreground italic bg-background/30 border border-border/30 rounded-lg p-4">
                    Kein Textinhalt vorhanden.
                  </p>
                )}
              </div>

              {/* Processed invoices */}
              {(detailEmail.processed_invoices || []).length > 0 && (
                <div>
                  <h4 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                    Verarbeitete Rechnungen ({detailEmail.processed_invoices.length})
                  </h4>
                  <div className="space-y-1.5">
                    {detailEmail.processed_invoices.map((pi, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between gap-2 text-sm bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-2.5"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="font-mono-data text-xs truncate">{pi.filename}</p>
                            {pi.vendor && <p className="text-xs text-muted-foreground truncate">{pi.vendor}</p>}
                          </div>
                        </div>
                        {typeof pi.gross_amount === 'number' && pi.gross_amount > 0 && (
                          <span className="font-mono-data text-sm whitespace-nowrap">
                            {pi.gross_amount.toFixed(2)} €
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Skipped duplicates */}
              {(detailEmail.skipped_duplicates || []).length > 0 && (
                <div>
                  <h4 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                    Übersprungene Duplikate ({detailEmail.skipped_duplicates.length})
                  </h4>
                  <div className="space-y-1.5">
                    {detailEmail.skipped_duplicates.map((d, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between gap-2 text-sm bg-amber-500/5 border border-amber-500/20 rounded-lg p-2.5"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <Info className="w-4 h-4 text-amber-400 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="font-mono-data text-xs truncate">{d.filename}</p>
                            <p className="text-xs text-muted-foreground">
                              {d.reason === 'duplicate_in_email' ? 'Doppelt in dieser Mail' : 'Schon in DB vorhanden'}
                              {d.invoice_number && ` · Rechn.-Nr. ${d.invoice_number}`}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDetailEmail(null)}
                data-testid="close-email-detail-btn"
              >
                Schließen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ==================== ASSIGN SUPPLIER DIALOG ==================== */}
      {assigningSupplier && (
        <Dialog open={!!assigningSupplier} onOpenChange={() => setAssigningSupplier(null)}>
          <DialogContent className="max-w-md w-[95vw] sm:w-full" data-testid="assign-supplier-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-amber-400" />
                Lieferant zuordnen
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Beleg: <strong className="text-foreground">{assigningSupplier.filename}</strong>
                {assigningSupplier.ocr_data?.vendor_name && (
                  <span className="block mt-1">OCR erkannt: <span className="text-primary">{assigningSupplier.ocr_data.vendor_name}</span></span>
                )}
              </p>
              {assigningSupplier.supplier_name && (
                <p className="text-xs text-emerald-400">Aktuell zugeordnet: {assigningSupplier.supplier_name}</p>
              )}
              <div className="space-y-2">
                <Label className="text-sm">Lieferant auswählen</Label>
                <Select onValueChange={v => handleAssignSupplier(assigningSupplier.id, v)}>
                  <SelectTrigger className="bg-background" data-testid="supplier-select">
                    <SelectValue placeholder="Kontakt wählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {contacts.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name} {c.company ? `(${c.company})` : ''} 
                        <span className="text-muted-foreground text-xs ml-1">
                          {c.contact_type === 'supplier' ? '- Lieferant' : c.contact_type === 'customer' ? '- Kunde' : ''}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAssigningSupplier(null)}>Abbrechen</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
